/**
 * Originally sourced from - https://github.com/sjinks/node-fetch-retry-ts
 *
 * NOTE: this is possible to just "install", but I brought the source code in to allow us to more easily troubleshoot
 * any issues we might encounter rather than guessing what the black box is doing.
 */
export type RequestDelayFunction = ( attempt: number, error: Error | null, response: Response | null ) => number;
export type RetryRequestFunction = (
    attempt: number,
    retries: number,
    error: Error | null,
    response: Response | null,
) => boolean;

/**
 * WHITELIST APPROACH:
 * We ONLY retry status codes explicitly listed in retryOn
 * Everything else (400, 429, 500, 502, 503, etc.) will NOT retry
 *
 * Current whitelist: 408 (Request Timeout), 504 (Gateway Timeout)
 * Excluded: 503 (Service Unavailable) - can indicate resource exhaustion or rate limiting, not transient issues
 */

export interface FetchRetryParams {
    retries?: number;
    retryDelay?: number | RequestDelayFunction;
    retryOn?: number[] | RetryRequestFunction; // DEPRECATED: Prefer using NON_RETRYABLE_STATUS_CODES instead
    maxRetryDelay?: number; // Maximum delay in ms to prevent excessive waiting
}

function sanitize( params: FetchRetryParams, defaults: Required< FetchRetryParams > ): Required< FetchRetryParams > {
    const result = { ...defaults, ...params };
    if ( typeof result.retries === 'undefined' ) {
        result.retries = defaults.retries;
    }

    if ( typeof result.retryDelay === 'undefined' ) {
        result.retryDelay = defaults.retryDelay;
    }

    if ( typeof result.retryOn === 'undefined' ) {
        result.retryOn = defaults.retryOn;
    }

    if ( typeof result.maxRetryDelay === 'undefined' ) {
        result.maxRetryDelay = defaults.maxRetryDelay;
    }

    return result;
}

export function
    fetchBuilder< F extends ( ...args: any ) => Promise< any > = typeof fetch >( fetchFunc: F, params: FetchRetryParams = {} ):
        ( input: Parameters< F >[ 0 ], init?: Parameters< F >[ 1 ] & FetchRetryParams ) => ReturnType< F > {
    const defaults = sanitize( params, {
        retries: 1, // Reduced from 2 to 1 - allows one retry (2 total attempts) to limit cascading failures
        retryDelay: 1000,
        retryOn: [ 408, 504 ], // WHITELIST: Only retry timeouts (removed 503 - can indicate resource exhaustion, not just temporary unavailability)
        maxRetryDelay: 2000
    } );

    return function ( input: Parameters<F>[0], init?: Parameters<F>[1] & FetchRetryParams ): ReturnType<F> {
        const frp = sanitize(
            {
                retries: init?.retries,
                retryDelay: init?.retryDelay,
                retryOn: init?.retryOn,
                maxRetryDelay: init?.maxRetryDelay,
            } satisfies FetchRetryParams,
            defaults
        );

        const retryDelayFn =
            typeof frp.retryDelay === 'function'
                ? ( attempt: number, error: Error | null, response: Response | null ): number => {
                    const delay = (frp.retryDelay as RequestDelayFunction)( attempt, error, response );
                    // Cap the delay at maxRetryDelay to prevent excessive waiting
                    return Math.min( delay, frp.maxRetryDelay );
                }
                : (): number => Math.min( frp.retryDelay as number, frp.maxRetryDelay );

        const retryOnFn =
            typeof frp.retryOn === 'function' ? frp.retryOn
                : ( attempt: number, retries: number, error: Error | null, response: Response | null ): boolean => {
                    // Don't retry if we've exceeded the retry limit
                    if ( attempt >= retries ) {
                        return false;
                    }

                    // WHITELIST: Only retry if status code is in the retryOn list
                    // Everything else (errors, 400, 429, 500, 502, 503, etc.) will NOT retry
                    if ( response && ( frp.retryOn as number[] ).includes( response.status ) ) {
                        return true;
                    }

                    // Don't retry network errors or anything else
                    return false;
                };

        return new Promise( function ( resolve, reject ): void {
            const extendedFetch = function ( attempt: number ): void {
                fetchFunc( input, init )
                    .then( function ( response: Response ): void {
                        if ( retryOnFn( attempt, frp.retries, null, response ) ) {
                            retry( attempt, null, response );
                        } else {
                            resolve( response );
                        }
                    } )
                    .catch( function ( error: unknown ): void {
                        const err = error instanceof Error ? error : new Error( String( error ) );
                        if ( retryOnFn( attempt, frp.retries, err, null ) ) {
                            // eslint-disable-next-line @typescript-eslint/no-use-before-define
                            retry( attempt, err, null );
                        } else {
                            reject( err );
                        }
                    } );
            };

            function retry( attempt: number, error: Error | null, response: Response | null ): void {
                setTimeout(
                    function (): void {
                        extendedFetch( ++attempt );
                    },
                    retryDelayFn( attempt, error, response ),
                );
            }

            extendedFetch( 0 );
        } ) as ReturnType< F >;
    };
}

export default fetchBuilder;